import {
  Avatar,
  Box,
  Button,
  FormLabel,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useSnackbar } from "notistack"
import { getStaff, removeStaff, getEmploymentContract, removeEmploymentContract } from "../api"
import LoadingContent from "../components/common/LoadingContent"
import TabPanel from "../components/ui/TabPanel"
import DeleteConfirm from "../components/modals/DeleteConfirm"

const StaffView = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState({})
  const [employmentContract, setEmploymentContract] = useState({})
  const [loading, setLoading] = useState(false)
  const { id } = useParams()

  const [value, setValue] = useState(0)
  const handleChange = (_, newValue) => setValue(newValue)
  const { enqueueSnackbar } = useSnackbar()

  const fetchUser = () => {
    setLoading(true)
    getStaff(id)
      .then((res) => {
        setUser(res.data)
      })
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setLoading(false))
  }

  const fetchEmploymentContract = () => {
    setLoading(true)
    getEmploymentContract(id)
      .then((res) => {
        setEmploymentContract(res.data)
      })
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchUser()
    fetchEmploymentContract()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showConfirmEmploymentContractDelete, setShowConfirmEmploymentContractDelete] = useState(false)

  const [removing, setRemoving] = useState(false)

  const deleteUser = () => {
    setRemoving(true)
    removeStaff(id)
      .then(() => navigate("/admin/staffs"))
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setRemoving(false))
  }

  const deleteEmploymentContract = () => {
    setRemoving(true)
    removeEmploymentContract(id)
      .then(() => navigate(`/admin/staffs/${id}`))
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setRemoving(false))
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0, sm: 10 },
        pl: 5,
        pt: 3,
      }}
    >
      <LoadingContent loading={loading}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            alignItems: "center",
          }}
        >
          <Avatar sx={{ width: 120, height: 120 }}>
            {user?.user_?.profilePicture}
          </Avatar>
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ mb: "10px" }} variant="h5">
              {user?.user_?.name}
            </Typography>

            <Detail label="Email" value={user?.user_?.email} />
            <Detail label="Phone" value={user.phone_number} />
          </Box>
        </Box>
      </LoadingContent>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={value} onChange={handleChange}>
            <Tab label="Profile" />
            <Tab label="Employment Details" />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <Box>
            <Typography variant="h6" sx={{ letterSpacing: 2 }} color="primary">
              PERSONAL DETAILS
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
              <Grid container spacing={1} mb={2}>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Name</FormLabel>
                    {user?.user_?.name}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Email</FormLabel>
                    {user?.user_?.email}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Phone</FormLabel>
                    {user.phone_number}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Emergency Contact</FormLabel>
                    {user.emergencyContact || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Emergency Contact Name</FormLabel>
                    {user.emergencyContactName || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Emergency Contact Relation</FormLabel>
                    {user.emergencyContactRelation || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Category</FormLabel>
                    {user.category || "N/A"}
                  </Stack>
                </Grid>
              </Grid>
              <Button
                onClick={() => setShowConfirmDelete(true)}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </Stack>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <Box>
            <Typography variant="h6" sx={{ letterSpacing: 2 }} color="primary">
              EMPLOYMENT DETAILS
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
              <Grid container spacing={1} mb={2}>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Designation</FormLabel>
                    {employmentContract?.designation}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Institute</FormLabel>
                    {employmentContract?.institution}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Contract Status</FormLabel>
                    {employmentContract?.contractStatus || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Joining Date</FormLabel>
                    {employmentContract?.contractStartDate ? employmentContract?.contractStartDate.split("T")[0] : "N/A"}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Last Working Date</FormLabel>
                    {employmentContract?.contractEndDate ? employmentContract?.contractEndDate.split("T")[0] : "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Aadhar No.</FormLabel>
                    {employmentContract?.aadhar || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>PAN</FormLabel>
                    {employmentContract?.pan || "N/A"}
                  </Stack>
                </Grid>
              </Grid>
              <Button
                onClick={() => setShowConfirmEmploymentContractDelete(true)}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </Stack>
          </Box>
        </TabPanel>
      </Box>
      <DeleteConfirm
        type="Staff"
        name={user?.user_?.name}
        isOpen={showConfirmDelete}
        close={() => setShowConfirmDelete(false)}
        action={deleteUser}
        loading={removing}
      />
      <DeleteConfirm
        type="Employment Contract"
        name={user?.user_?.name}
        isOpen={showConfirmEmploymentContractDelete}
        close={() => setShowConfirmEmploymentContractDelete(false)}
        action={deleteEmploymentContract}
        loading={removing}
      />
    </Box>
  )
}

const Detail = ({ label, value }) => (
  <Stack direction="row" spacing={2} sx={{ maxWidth: "14rem" }}>
    <Typography variant="body1" color="gray">
      {label}
    </Typography>
    <Typography
      variant="body1"
      style={{
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Stack>
)

export default StaffView
